// How a receipt-level discount reads in the payment screen's picker.
//
// suggestDiscounts offers every active RECEIPT-scope campaign that matches the
// patient and the lines — including ones that would apply 0 baht to THIS receipt
// (its own maxDiscountPercent ceiling would be breached, or the lines have
// nothing left to discount). Those used to render as "-0 บาท" with no
// explanation, and the operator only learned the real reason from the save.
//
// The server now attaches `notApplicable: { code, message }` to exactly those,
// from the same table the save quotes, so the picker and the save agree.
//
// Relative imports only in this directory — the logic specs run under plain
// Playwright/Node with no webpack, so "@/..." does not resolve there.

const appliedOf = (d) => (d && d.calculated && d.calculated.appliedVoucherTotal) || 0;

// `notApplicable` is the authority when present. A 0-baht discount from an older
// backend that does not send the field is still not money off, so it is marked
// unusable too — it just has nothing to explain.
const isUnusable = (d) => !!(d && d.notApplicable) || appliedOf(d) <= 0;

/**
 * @param {{name: string, calculated?: {appliedVoucherTotal?: number},
 *   notApplicable?: {code: string, message: string}|null}} d
 * @returns {string} the dropdown option text
 */
export function receiptDiscountOptionLabel(d) {
  const name = (d && d.name) || "";
  if (isUnusable(d)) {
    // Deliberately not "-0 บาท": that reads as a discount worth nothing rather
    // than one that cannot be used, and invites the operator to pick it anyway.
    return `${name} (ใช้ไม่ได้กับใบเสร็จนี้)`;
  }
  return `${name} (-${appliedOf(d).toLocaleString()} บาท)`;
}

/**
 * The server's reason for a discount that would apply nothing, to show beside
 * the picker. Empty when there is nothing to say — no selection, a usable
 * discount, or a backend that does not send the field.
 *
 * @param {object|null|undefined} d
 * @returns {string}
 */
export function receiptDiscountNotice(d) {
  return (d && d.notApplicable && d.notApplicable.message) || "";
}
